import React from 'react'
import { List, fromJS, Map } from 'immutable'

import moment from '../../lib/moment'

import {
  PPS_ALERT,
  PPS_CHANGE_DISPLAY_TAB,
  PPS_SET_SUBMIT_MONTH,
  PPS_SET_OTHER_COST_EDITING,
  PPS_RESET_OTHER_COST_EDITING,
  PPS_RESET_ALERT,
  PPS_IS_FETCHING,
  PPS_FETCH_PROJECTS_SUCCESS,
  PPS_FETCH_PROFIT_SUCCESS,
  PPS_UPDATE_PROFIT_SUCCESS,
  PPS_SUBMIT_PROFIT_SUCCESS,
  PPS_IMPORT_DETAIL_SUCCESS,
  PPS_FETCH_PROFIT_FAILURE,
  PPS_IMPORT_DETAIL_CLEAR,
  PPS_DELETE_IMPORT_SUCCESS,
  PPS_GET_PROJECT_PROGRESS_REQUEST,
  PPS_GET_PROJECT_PROGRESS_SUCCESS,
  PPS_GET_PROJECT_PROGRESS_CLEAR,
  PPS_GET_PROJECT_PROGRESS_FAILURE,
  PPS_EDIT_PROJECT_PROGRESS_REQUEST,
  PPS_EDIT_PROJECT_PROGRESS_SUCCESS,
  PPS_EDIT_PROJECT_PROGRESS_FAILURE,
  PPS_EDIT_PROJECT_PROGRESS_CLEAR,
  PPS_MANUAL_OPEN,
  PPS_MANUAL_CLOSE,
  PPS_SET_OTHER_COST,
  PPS_SET_PERCENT_TABLE_EXPAND,
  PPS_CAN_SAVE_PROGRESS,
  PPPS_OPEN_RESPONSE_MODAL,
} from '../../constants'

import ppsImportActualReducer from './pps-import-actual-reducer'

/**
 * @typedef {object} AlertModal
 * @property {string} name
 * @property {string} header
 * @property {import('react').ReactNode} body
 * @property {number} [statusCode]
 * @property {string} [cancelText]
 * @property {string} [confirmText]
 * @property {string} [project_id]
 */

const initialState = fromJS({
  pps_import_actual: ppsImportActualReducer(),

  is_fetching: false,
  is_manual_open: false,
  show_accounting: false,
  display: 'overview',
  profit_data: {},
  other_cost_editing: List(),
  submit_month: moment().startOf('month'),
  overviews: [],
  submits: [],
  progresses: {},
  updateProjectProgress: {},
  accounting_costs: [],
  import_detail: { transactions: [] },
  unapproved_timesheets: [],
  alert: /** @type {AlertModal} */ (null),
  percent_table: { expand: {}, is_delta: true },
  can_save_progress: true,
  response_modal: { is_open: false, res: {} },
})

function ppsReducer(state = initialState, action) {
  if (action.type.includes('PPS_IMPORT_ACTUAL')) {
    return state.update('pps_import_actual', pps_import_actual =>
      ppsImportActualReducer(pps_import_actual, action)
    )
  }

  switch (action.type) {
    case PPS_IS_FETCHING:
      return state.set('is_fetching', true)
    case PPS_FETCH_PROJECTS_SUCCESS: {
      const {
        projectProfitOverviews,
        projectProfitSubmits,
        accountingCosts,
      } = action.payload.projectProfits
      const { can_display_accounting_tab } = action.payload
      return state
        .set('is_fetching', false)
        .set('overviews', fromJS(projectProfitOverviews))
        .set('submits', fromJS(projectProfitSubmits))
        .set('accounting_costs', fromJS(accountingCosts))
        .set('show_accounting', Boolean(can_display_accounting_tab))
    }
    case PPS_FETCH_PROFIT_SUCCESS: {
      const { projectProfit, unApproveTimesheetUsers } = action.payload
      return (
        state
          .set('is_fetching', false)
          // .set('alert', null)
          .set(
            'profit_data',
            fromJS(projectProfit).update('other_costs', ocs =>
              ocs.sortBy(
                rec => moment(rec.get('imported_date')).toDate(),
                (a, b) => (a < b ? 1 : a > b ? -1 : 0)
              )
            )
          )
          .set('unapproved_timesheets', fromJS(unApproveTimesheetUsers))
          .set('other_cost_editing', fromJS(projectProfit.other_costs || []))
      )
    }
    case PPS_FETCH_PROFIT_FAILURE: {
      const { error: header, message: body } = action.payload.response
      return state
        .set('is_fetching', false)
        .set(
          'alert',
          fromJS({
            name: PPS_ALERT.NOT_SUBMIT_INFO,
            header,
            body,
            cancelText: 'CANCEL',
            confirmText: 'GO TO PROJ. INFO',
          })
        )
        .set('profit_data', fromJS({}))
        .set('other_cost_editing', fromJS([]))
    }
    case PPS_UPDATE_PROFIT_SUCCESS:
      return state.set('is_fetching', false)
    case PPS_SUBMIT_PROFIT_SUCCESS: {
      const { project_id } = action.payload.submittedProjectProfit
      return state.set('is_fetching', false).set(
        'alert',
        Map({
          name: PPS_ALERT.SUBMIT_PROFIT,
          header: 'Project Profit Sheet Submitted',
          body: (
            <span>
              You’ve successfully submitted
              <b> {moment(state.get('submit_month')).format('MMM YYYY')} </b>
              Project Profit Sheet
            </span>
          ),
          cancelText: 'Close',
          confirmText: 'Back to lists',
          project_id,
        })
      )
    }
    case PPS_IMPORT_DETAIL_SUCCESS: {
      const { projectProfitAccounting } = action.payload
      return state.set('is_fetching', false).set('import_detail', fromJS(projectProfitAccounting))
    }
    case PPS_GET_PROJECT_PROGRESS_REQUEST:
      return state.set('is_fetching', true)
    case PPS_GET_PROJECT_PROGRESS_SUCCESS: {
      const { projectProgress } = action.payload
      return state
        .set('is_fetching', false)
        .set('progresses', fromJS(projectProgress))
        .set('is_submit_pps', projectProgress.is_submit_pps)
    }
    case PPS_GET_PROJECT_PROGRESS_FAILURE: {
      return state
        .set('is_fetching', false)
        .set('progresses',
          Map({
            type: 'error',
            status: 'Error',
            progresses: fromJS(action.payload.message),
            message: fromJS(action.payload.response.message),
          })
        )
    }
    case PPS_GET_PROJECT_PROGRESS_CLEAR:
      return state.set('progresses', fromJS({}))
    case PPS_EDIT_PROJECT_PROGRESS_REQUEST:
      return state.set('is_fetching', true)
    case PPS_EDIT_PROJECT_PROGRESS_SUCCESS: {
      const { updateProjectProgress } = action.payload
      return state.set('is_fetching', false).set('updateProjectProgress', fromJS(updateProjectProgress))
    }
    case PPS_EDIT_PROJECT_PROGRESS_FAILURE: {
      return state
        .set('is_fetching', false)
        .set('updateProjectProgress',
          Map({
            type: 'error',
            status: 'Error',
            updateProjectProgress: fromJS(action.payload.message),
            message: fromJS(action.payload.response.message),
          })
        )
    }
    case PPS_EDIT_PROJECT_PROGRESS_CLEAR:
      return state.set('updateProjectProgress', fromJS({}))
    case PPS_DELETE_IMPORT_SUCCESS:
      return state.set('is_fetching', false)
    case PPS_CHANGE_DISPLAY_TAB:
      return state.set('display', action.payload)
    case PPS_SET_SUBMIT_MONTH:
      return state.set('submit_month', action.payload)
    case PPS_SET_OTHER_COST:
      return state.setIn(['profit_data', 'other_costs'], fromJS(action.payload))
    case PPS_SET_OTHER_COST_EDITING:
      return state.set('other_cost_editing', action.payload)
    case PPS_RESET_OTHER_COST_EDITING:
      return state.set('other_cost_editing', state.getIn(['profit_data', 'other_costs']))
    case PPS_RESET_ALERT:
      return state.set('alert', null)
    case PPS_IMPORT_DETAIL_CLEAR:
      return state.set('import_detail', initialState.get('import_detail'))
    case PPS_MANUAL_OPEN:
      return state.set('is_manual_open', true)
    case PPS_MANUAL_CLOSE:
      return state.set('is_manual_open', false)
    case PPS_SET_PERCENT_TABLE_EXPAND:
      return state.set('percent_table', fromJS(action.payload))
    case PPS_CAN_SAVE_PROGRESS:
      return state.set('can_save_progress', fromJS(action.payload))
    case PPPS_OPEN_RESPONSE_MODAL:
      return state.set('response_modal', fromJS(action.payload))
    default:
      return state
  }
}

export default ppsReducer



// WEBPACK FOOTER //
// ./src/reducers/project-profit-sheet/index.js